import type { GroupAnalysisSpanData } from '@/generated/types/navigator/types.gen';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  setWorklogItems,
  setWorklogPage,
  useFetchWorkLogsQuery,
} from '@/features/worklogSlice';
import { Section } from '@/views/list/level3/Section';
import type { WorklogActivityProps } from '@/views/map/worklog/WorklogActivity/WorklogActivity';
import { WorklogActivity } from '@/views/map/worklog/WorklogActivity/WorklogActivity';
import { WorkLogEntityType } from '@/generated/types/navigator/types.gen';
import { getProjectRouteScope } from '@/helpers/projectRouteScope';
import { Text } from '@mantine/core';

type ActivityProps = {
  a: GroupAnalysisSpanData;
};

export const ActivityTab = ({ a }: ActivityProps) => {
  const dispatch = useAppDispatch();
  const { worklogItems, worklogPage } = useAppSelector((s) => s.worklogMap);
  const { planId } = getProjectRouteScope(useLocation().pathname);
  const LIMIT = 10;
  const { data, isLoading, isFetching, originalArgs } = useFetchWorkLogsQuery({
    entity_type: WorkLogEntityType.SPAN,
    entity_id: a.level3,
    worklogPage,
    limit: LIMIT,
    plan_id: planId,
  });
  const [summary, setSummary] = useState<{ total: number }>({ total: 0 });

  const hasMoreToLoad = useCallback(() => {
    return summary && summary.total > 0 && worklogItems.length < summary.total;
  }, [worklogItems.length, summary]);

  const observer = useRef<IntersectionObserver | null>(null);

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetching) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreToLoad()) {
          dispatch(setWorklogPage(worklogPage + 1));
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, isFetching, hasMoreToLoad]
  );

  const itemsExists = () => {
    return data?.data
      ? data?.data.findIndex((el) => worklogItems.some((o) => o.id === el.id))
      : -1;
  };

  useEffect(() => {
    if (data && data?.data) {
      const results = data.data;
      const queryPage = originalArgs?.worklogPage;
      setSummary(data.summary);
      if (worklogPage !== queryPage) return;
      if (worklogPage === 1) {
        dispatch(setWorklogItems(results));
      } else if (
        itemsExists() === -1 &&
        worklogItems.length + results.length < worklogPage * LIMIT
      ) {
        dispatch(setWorklogItems([...worklogItems, ...results]));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, originalArgs, originalArgs?.worklogPage]);

  const renderWorklogItems = useCallback(() => {
    return worklogItems.map((wl, idx) => {
      const props: WorklogActivityProps = {
        worklog: wl,
      };

      if (worklogItems.length === idx + 1) {
        props.itemRef = lastItemRef;
      }

      return <WorklogActivity key={wl.id} {...props} />;
    });
  }, [worklogItems, lastItemRef]);

  return (
    <Section>
      {worklogItems.length > 0 ? (
        renderWorklogItems()
      ) : (
        <Text c="dimmed" ta="center" fz="sm">
          {isLoading && <>Loading...</>}
          {!isLoading && !isFetching && (
            <>
              No activity added to this span yet. Tap "Add activity" to log work
              or inspection performed.
            </>
          )}
        </Text>
      )}
    </Section>
  );
};
